/**
 * ══════════════════════════════════════════════════════════════
 * PREDICTIVE SEARCH ENGINE — Phone Safe v3.1
 * Extraído para asset separado | Desktop + Mobile
 * ──────────────────────────────────────────────────────────────
 * Funcionalidades:
 * • Duas instâncias independentes (desktop + mobile)
 * • Idle state: Recentes (localStorage) + Tendências + Categorias
 * • Resultados reais via Shopify /search/suggest.json
 * • Cache com TTL (5 min, max 50 entradas)
 * • AbortController sem leaks
 * • Fetch com retry (1 tentativa)
 * • Keyboard nav robusta (wrap-around, scrollIntoView smooth)
 * • ARIA live region para screen readers
 * • addRecent case-insensitive
 * • Tendências dinâmicas via Liquid (bridge: window.__PS_TRENDING)
 *
 * Dependências no HTML:
 * - #ps-search-input + #ps-suggest-box (desktop)
 * - #ps-search-input-mobile + #ps-suggest-box-mobile (mobile)
 * - #ps-live-region (ARIA)
 * - window.__PS_TRENDING (string, separador '|||', set by Liquid)
 * ══════════════════════════════════════════════════════════════
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════
     CONFIG
     ═══════════════════════════════════════════ */
  var DEBOUNCE   = 250;
  var MIN_CHARS  = 2;
  var API_LIMIT  = 6;
  var MAX_RECENT = 5;
  var CACHE_TTL  = 5 * 60 * 1000;
  var LS_KEY     = 'pss_r';
  var liveRegion = document.getElementById('ps-live-region');

  /* ═══════════════════════════════════════════
     TENDÊNCIAS — dinâmicas via Liquid bridge
     Lê window.__PS_TRENDING (definido inline no header.liquid)
     Fallback para lista hardcoded se vazio
     ═══════════════════════════════════════════ */
  var TRENDING_RAW = (typeof window.__PS_TRENDING === 'string') ? window.__PS_TRENDING : '';
  var TRENDING = TRENDING_RAW.length > 0
    ? TRENDING_RAW.split('|||').slice(0, 6)
    : ['iPhone 16 Pro', 'Carregador MagSafe', 'Película Vidro', 'AirPods', 'Capa iPhone'];

  /* ═══════════════════════════════════════════
     CATEGORIAS (fixas)
     ═══════════════════════════════════════════ */
  var CATEGORIES = [
    { label: 'iPhones', q: 'iPhone', icon: 'smartphone' },  
    { label: 'Capas & Proteção', q: 'capa',      icon: 'shield' },
    { label: 'Carregadores',     q: 'carregador', icon: 'zap'    },
    { label: 'Películas',        q: 'película',   icon: 'layers' },
    { label: 'Cabos',            q: 'cabo',       icon: 'link'   },  
  ];

  /* ═══════════════════════════════════════════
     SVG ICON MAP
     ═══════════════════════════════════════════ */
  var ICONS = {
    shield: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
    zap:    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
    layers: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>',
    grid:   '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>',
    link:   '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
    clock:  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    trend:  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
    chevR:  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>',
    smartphone: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>',
  };

  /* ═══════════════════════════════════════════
     ARIA LIVE REGION
     ═══════════════════════════════════════════ */
  function announce(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  /* ═══════════════════════════════════════════
     LOCALSTORAGE — Recentes
     ═══════════════════════════════════════════ */
  function getRecent() {
    try { var d = JSON.parse(localStorage.getItem(LS_KEY)); return Array.isArray(d) ? d.slice(0, MAX_RECENT) : []; }
    catch (e) { return []; }
  }
  function saveRecent(l) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(l.slice(0, MAX_RECENT))); } catch (e) { /* silêncio */ }
  }
  function addRecent(q) {
    if (!q || !q.trim()) return;
    var t = q.trim();
    var l = getRecent().filter(function (s) { return s.toLowerCase() !== t.toLowerCase(); });
    l.unshift(t);
    saveRecent(l);
  }
  function clearRecent() {
    try { localStorage.removeItem(LS_KEY); } catch (e) { /* silêncio */ }
  }

  /* ═══════════════════════════════════════════
     PRICE FORMATTER
     ═══════════════════════════════════════════ */
  function formatPrice(p) {
    return parseFloat(p).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
  }

  /* ═══════════════════════════════════════════
     HTML ESCAPE (singleton)
     ═══════════════════════════════════════════ */
  var _escDiv = document.createElement('div');
  function esc(s) { _escDiv.textContent = s; return _escDiv.innerHTML; }

  /* ═══════════════════════════════════════════
     CACHE COM TTL
     ═══════════════════════════════════════════ */
  function Cache() { this._d = {}; }
  Cache.prototype.get = function (k) {
    var e = this._d[k];
    if (!e) return null;
    if (Date.now() - e.ts > CACHE_TTL) { delete this._d[k]; return null; }
    return e.v;
  };
  Cache.prototype.set = function (k, v) {
    this._d[k] = { v: v, ts: Date.now() };
    var ks = Object.keys(this._d);
    if (ks.length > 50) delete this._d[ks[0]];
  };

  /* ═══════════════════════════════════════════
     PREDICTIVE SEARCH — Constructor
     One instance per search bar
     ═══════════════════════════════════════════ */
  function PS(inp, box) {
    if (!inp || !box) return;
    var s = this;
    s.input = inp;
    s.box = box;
    s.form = inp.closest('form');
    s.wrap = inp.closest('.ps-search-wrap');
    s.clearBtn = s.wrap ? s.wrap.querySelector('.ps-search-clear') : null;
    s.abortCtrl = null;
    s.timer = null;
    s.cache = new Cache();
    s.navIdx = -1;
    s.isOpen = false;
    s.lastQ = '';

    /* Event listeners */
    s.input.addEventListener('input', function () { s._onInput(); });
    s.input.addEventListener('focus', function () { s._onFocus(); });
    s.input.addEventListener('keydown', function (e) { s._onKey(e); });

    if (s.clearBtn) {
      s.clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        s.input.value = '';
        s.lastQ = '';
        s._toggleClear();
        s._showIdle();
        s.input.focus();
      });
    }

    if (s.form) {
      s.form.addEventListener('submit', function () {
        var q = s.input.value.trim();
        if (q.length > 0) addRecent(q);
      });
    }

    /* Close on outside click (capture phase) */
    document.addEventListener('click', function (e) {
      if (!s.wrap || !s.wrap.contains(e.target)) s._close();
    }, true);
  }

  /* ═══════════════════════════════════════════
     CLEAR BUTTON
     ═══════════════════════════════════════════ */
  PS.prototype._toggleClear = function () {
    if (this.clearBtn) this.clearBtn.style.display = this.input.value.length > 0 ? 'flex' : 'none';
  };

  /* ═══════════════════════════════════════════
     OPEN / CLOSE
     ═══════════════════════════════════════════ */
  PS.prototype._open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.box.style.display = 'block';
    this.box.offsetHeight; /* force reflow for transition */
    this.box.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
    if (this.wrap) this.wrap.classList.add('ps-search-wrap--active');
  };

  PS.prototype._close = function () {
    if (!this.isOpen) return;
    var s = this;
    s.isOpen = false;
    s.box.classList.remove('is-open');
    s.input.setAttribute('aria-expanded', 'false');
    if (s.wrap) s.wrap.classList.remove('ps-search-wrap--active');
    s.navIdx = -1;
    s._abort();
    setTimeout(function () {
      if (!s.isOpen) s.box.style.display = 'none';
    }, 220);
  };

  /* ═══════════════════════════════════════════
     ABORT HELPER
     ═══════════════════════════════════════════ */
  PS.prototype._abort = function () {
    if (this.abortCtrl) {
      this.abortCtrl.abort();
      this.abortCtrl = null;
    }
  };

  /* ═══════════════════════════════════════════
     INPUT / FOCUS HANDLERS
     ═══════════════════════════════════════════ */
  PS.prototype._onInput = function () {
    var s = this;
    clearTimeout(s.timer);
    s._toggleClear();
    var q = s.input.value.trim();
    if (q.length < MIN_CHARS) {
      s._abort();
      s.lastQ = '';
      s._showIdle();
      return;
    }
    if (q === s.lastQ && s.isOpen) return;
    s.timer = setTimeout(function () {
      s.lastQ = q;
      s._fetch(q, 0);
    }, DEBOUNCE);
  };

  PS.prototype._onFocus = function () {
    this._toggleClear();
    var q = this.input.value.trim();
    if (q.length >= MIN_CHARS) {
      var c = this.cache.get(q);
      if (c !== null) this._renderProducts(c, q);
      else this._fetch(q, 0);
    } else {
      this._showIdle();
    }
  };

  /* ═══════════════════════════════════════════
     KEYBOARD NAVIGATION — Wrap-around
     ═══════════════════════════════════════════ */
  PS.prototype._onKey = function (e) {
    if (e.key === 'Escape') { this._close(); this.input.blur(); return; }
    if (e.key === 'Tab') { this._close(); return; }

    var items = this.box.querySelectorAll('[data-ps-nav]');
    if (!items.length) return;
    var len = items.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.navIdx >= len - 1) {
        this.navIdx = -1;
        this.input.focus();
        this._clearHL(items);
      } else {
        this.navIdx++;
        this._hl(items);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.navIdx <= 0) {
        this.navIdx = -1;
        this.input.focus();
        this._clearHL(items);
      } else {
        this.navIdx--;
        this._hl(items);
      }
    } else if (e.key === 'Enter' && this.navIdx >= 0 && items[this.navIdx]) {
      e.preventDefault();
      items[this.navIdx].click();
    }
  };

  PS.prototype._hl = function (items) {
    this._clearHL(items);
    var it = items[this.navIdx];
    if (!it) return;
    it.classList.add('ps-suggest-item--focus');
    it.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', it.id || '');
    if (it.scrollIntoView) it.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  PS.prototype._clearHL = function (items) {
    for (var i = 0; i < items.length; i++) {
      items[i].classList.remove('ps-suggest-item--focus');
      items[i].setAttribute('aria-selected', 'false');
    }
    this.input.removeAttribute('aria-activedescendant');
  };

  /* ═══════════════════════════════════════════
     IDLE STATE — Recentes + Trending + Categorias
     ═══════════════════════════════════════════ */
  PS.prototype._showIdle = function () {
    var s = this, frag = document.createDocumentFragment(), nc = 0;

    /* ── Recentes ── */
    var recents = getRecent();
    if (recents.length > 0) {
      frag.appendChild(s._sec('Recentes', ICONS.clock));

      recents.forEach(function (term) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ps-suggest-row ps-suggest-row--recent';
        btn.setAttribute('data-ps-nav', '');
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', 'false');
        btn.id = 'ps-nav-' + (nc++);
        btn.innerHTML =
          '<span class="ps-suggest-row__icon ps-suggest-row__icon--muted">' + ICONS.clock + '</span>' +
          '<span class="ps-suggest-row__label">' + esc(term) + '</span>' +
          '<span class="ps-suggest-row__chevron">' + ICONS.chevR + '</span>';
        btn.addEventListener('click', function () { s._go(term); });
        frag.appendChild(btn);
      });

      /* Limpar recentes — com role + aria-label */
      var clr = document.createElement('button');
      clr.type = 'button';
      clr.className = 'ps-suggest-clear-btn';
      clr.setAttribute('role', 'button');
      clr.setAttribute('aria-label', 'Limpar pesquisas recentes');
      clr.textContent = 'Limpar recentes';
      clr.addEventListener('click', function () {
        clearRecent();
        announce('Pesquisas recentes apagadas');
        s._showIdle();
      });
      frag.appendChild(clr);
    }

    /* ── Em Tendência ── */
    frag.appendChild(s._sec('Em Tendência', ICONS.trend));
    var pw = document.createElement('div');
    pw.className = 'ps-suggest-pills';
    TRENDING.forEach(function (term) {
      var p = document.createElement('button');
      p.type = 'button';
      p.className = 'ps-suggest-pill';
      p.setAttribute('data-ps-nav', '');
      p.setAttribute('role', 'option');
      p.setAttribute('aria-selected', 'false');
      p.id = 'ps-nav-' + (nc++);
      p.textContent = term;
      p.addEventListener('click', function () { s._go(term); });
      pw.appendChild(p);
    });
    frag.appendChild(pw);

    /* ── Categorias ── */
    frag.appendChild(s._sec('Categorias', null));
    var cl = document.createElement('div');
    cl.className = 'ps-suggest-cats';
    CATEGORIES.forEach(function (cat) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ps-suggest-cat';
      b.setAttribute('data-ps-nav', '');
      b.setAttribute('role', 'option');
      b.setAttribute('aria-selected', 'false');
      b.id = 'ps-nav-' + (nc++);
      b.innerHTML =
        '<span class="ps-suggest-cat__icon">' + (ICONS[cat.icon] || '') + '</span>' +
        '<span class="ps-suggest-cat__label">' + esc(cat.label) + '</span>' +
        '<span class="ps-suggest-cat__chevron">' + ICONS.chevR + '</span>';
      b.addEventListener('click', function () { s._go(cat.q); });
      cl.appendChild(b);
    });
    frag.appendChild(cl);

    s.box.innerHTML = '';
    s.box.appendChild(frag);
    s.navIdx = -1;
    s._open();
    announce('Sugestões de pesquisa disponíveis');
  };

  /* ═══════════════════════════════════════════
     FETCH — Shopify API com retry (1 tentativa)
     ═══════════════════════════════════════════ */
  PS.prototype._fetch = function (q, retryCount) {
    var s = this;
    var cached = s.cache.get(q);
    if (cached !== null) { s._renderProducts(cached, q); return; }

    s._abort();
    s.abortCtrl = new AbortController();
    s._showLoading();

    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=' + API_LIMIT +
      '&resources[options][unavailable_products]=last';

    fetch(url, { signal: s.abortCtrl.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        s.abortCtrl = null;
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        s.cache.set(q, products);
        s._renderProducts(products, q);
      })
      .catch(function (err) {
        s.abortCtrl = null;
        if (err.name === 'AbortError') return;
        /* Retry uma vez em caso de erro temporário */
        if (retryCount < 1) {
          setTimeout(function () { s._fetch(q, retryCount + 1); }, 500);
        } else {
          s._showError();
        }
      });
  };

  /* ═══════════════════════════════════════════
     RENDER: Products from API
     ═══════════════════════════════════════════ */
  PS.prototype._renderProducts = function (products, query) {
    var s = this, frag = document.createDocumentFragment(), nc = 0;
    if (!products.length) { s._showEmpty(query); return; }

    /* Header */
    var hdr = document.createElement('div');
    hdr.className = 'ps-suggest-sec-header ps-suggest-sec-header--sticky';
    hdr.innerHTML =
      '<span class="ps-suggest-sec-label">Produtos</span>' +
      '<span class="ps-suggest-sec-count">' + products.length + '</span>';
    frag.appendChild(hdr);

    /* Product items */
    products.forEach(function (p) {
      var a = document.createElement('a');
      a.href = p.url;
      a.className = 'ps-suggest-product';
      a.setAttribute('data-ps-nav', '');
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.id = 'ps-nav-' + (nc++);
      a.addEventListener('click', function () { addRecent(query); });

      /* Image */
      if (p.image) {
        var img = document.createElement('img');
        img.src = p.image;
        img.alt = p.title;
        img.loading = 'lazy';
        img.width = 52;
        img.height = 52;
        img.className = 'ps-suggest-product__img';
        a.appendChild(img);
      } else {
        var ni = document.createElement('div');
        ni.className = 'ps-suggest-product__noimg';
        ni.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';
        a.appendChild(ni);
      }

      /* Info */
      var info = document.createElement('div');
      info.className = 'ps-suggest-product__info';

      var title = document.createElement('div');
      title.className = 'ps-suggest-product__title';
      title.textContent = p.title;
      title.setAttribute('title', p.title);
      info.appendChild(title);

      if (p.price) {
        var pr = document.createElement('div');
        pr.className = 'ps-suggest-product__price';
        pr.textContent = formatPrice(p.price);
        info.appendChild(pr);
      }

      a.appendChild(info);

      /* Chevron */
      var ch = document.createElement('span');
      ch.className = 'ps-suggest-product__chevron';
      ch.innerHTML = ICONS.chevR;
      a.appendChild(ch);

      frag.appendChild(a);
    });

    /* Footer — "Ver todos os resultados" */
    var ft = document.createElement('div');
    ft.className = 'ps-suggest-footer';
    var fl = document.createElement('a');
    fl.href = '/search?type=product&q=' + encodeURIComponent(query);
    fl.className = 'ps-suggest-footer__link';
    fl.setAttribute('data-ps-nav', '');
    fl.setAttribute('role', 'option');
    fl.setAttribute('aria-selected', 'false');
    fl.id = 'ps-nav-' + (nc++);
    fl.innerHTML = 'Ver todos os resultados ' + ICONS.chevR;
    fl.addEventListener('click', function () { addRecent(query); });
    ft.appendChild(fl);
    frag.appendChild(ft);

    s.box.innerHTML = '';
    s.box.appendChild(frag);
    s.navIdx = -1;
    s._open();
    announce(products.length + (products.length === 1 ? ' produto encontrado' : ' produtos encontrados'));
  };

  /* ═══════════════════════════════════════════
     RENDER: Loading / Empty / Error
     ═══════════════════════════════════════════ */
  PS.prototype._showLoading = function () {
    this.box.innerHTML =
      '<div class="ps-suggest-status">' +
        '<span class="ps-spinner" aria-hidden="true"></span>' +
        '<span>A pesquisar...</span>' +
      '</div>';
    this._open();
    announce('A pesquisar produtos');
  };

  PS.prototype._showEmpty = function (query) {
    var s = this, frag = document.createDocumentFragment();

    var em = document.createElement('div');
    em.className = 'ps-suggest-empty-state';
    em.innerHTML =
      '<div class="ps-suggest-empty-state__icon">' +
        '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>' +
      '</div>' +
      '<p class="ps-suggest-empty-state__title">Sem resultados para</p>' +
      '<p class="ps-suggest-empty-state__query">\u201c' + esc(query) + '\u201d</p>';
    frag.appendChild(em);

    var pw = document.createElement('div');
    pw.className = 'ps-suggest-pills ps-suggest-pills--centered';
    TRENDING.slice(0, 4).forEach(function (t) {
      var p = document.createElement('button');
      p.type = 'button';
      p.className = 'ps-suggest-pill';
      p.setAttribute('data-ps-nav', '');
      p.setAttribute('role', 'option');
      p.setAttribute('aria-selected', 'false');
      p.textContent = t;
      p.addEventListener('click', function () { s._go(t); });
      pw.appendChild(p);
    });
    frag.appendChild(pw);

    s.box.innerHTML = '';
    s.box.appendChild(frag);
    s.navIdx = -1;
    s._open();
    announce('Sem resultados encontrados para ' + query);
  };

  PS.prototype._showError = function () {
    this.box.innerHTML =
      '<div class="ps-suggest-status ps-suggest-status--error">' +
        '<span>Erro ao pesquisar. Tenta novamente.</span>' +
      '</div>';
    this._open();
    announce('Erro ao pesquisar. Tenta novamente.');
  };

  /* ═══════════════════════════════════════════
     HELPERS
     ═══════════════════════════════════════════ */
  PS.prototype._go = function (term) {
    addRecent(term);
    this.input.value = term;
    this._toggleClear();
    this._close();
    window.location.href = '/search?type=product&q=' + encodeURIComponent(term);
  };

  PS.prototype._sec = function (label, icon) {
    var h = document.createElement('div');
    h.className = 'ps-suggest-sec-header';
    h.innerHTML =
      (icon ? '<span class="ps-suggest-sec-icon">' + icon + '</span>' : '') +
      '<span class="ps-suggest-sec-label">' + esc(label) + '</span>';
    return h;
  };

  /* ═══════════════════════════════════════════
     INIT — Desktop + Mobile instances
     ═══════════════════════════════════════════ */
  var di = document.getElementById('ps-search-input');
  var db = document.getElementById('ps-suggest-box');
  var mi = document.getElementById('ps-search-input-mobile');
  var mb = document.getElementById('ps-suggest-box-mobile');

  if (di && db) new PS(di, db);
  if (mi && mb) new PS(mi, mb);

})();